import { useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import { RefreshCw } from 'lucide-react';
import { Button, Dialog, Select, Switch, TextField } from '@nyce/admin-kit/ui';
import {
  ActionsRow,
  ConfirmButton,
  EmptyState,
  ErrorBox,
  ListField,
  Loading,
  Page,
  SortSelect,
  SortTh,
  TableScroller,
  useAsync,
  useNotice,
  useSort,
} from '../../components/common';
import { formatBytes, formatDateTime, formatDuration, formatRelative } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import {
  automationGw,
  type Agent,
  type AgentInput,
  type AutomationGwApi,
  type ContainerAuthClient,
  type TunnelStatus,
} from '../api/client';
import {
  AuthChip,
  ConnectionChip,
  RemoteCell,
  VersionCell,
  canMoveToAuth,
  isContainer,
  agPath,
  agentInfos,
  isOutdated,
  tunnelOf,
  useAutomationTarget,
  useAutomationWrite,
} from '../shared';
import '../automation.css';

const STATES = [
  { value: '', label: 'All agents' },
  { value: 'connected', label: 'Connected' },
  { value: 'offline', label: 'Offline' },
  { value: 'outdated', label: 'Update available' },
  { value: 'site', label: 'Needs a site visit' },
  { value: 'config', label: 'Config at risk' },
  { value: 'secret', label: 'Shared secret only' },
  { value: 'auth', label: 'On AUTH' },
];

const EMPTY: AgentInput = {
  tenantId: '',
  clientId: '',
  displayName: '',
  sharedSecret: '',
  allowedIpRanges: [],
};

/** The registered agents with their live tunnel, version and system info; restart, update, edit, secrets. */
export function AgentsPage() {
  const target = useAutomationTarget();
  const api = useMemo(() => automationGw(target), [target]);
  const canWrite = useAutomationWrite();
  const toast = useNotice();
  const [tenant, setTenant] = useParam('tenant', '');
  const [state, setState] = useParam('state', '');
  const [q, setQ] = useParam('q', '');
  const [open] = useParam('agent', '');
  const data = useAsync(async () => {
    const [agents, tenants, tunnels, latest] = await Promise.all([
      api.agents(),
      api.tenants(),
      api.tunnels(),
      api.latest().catch(() => null),
    ]);
    return { agents, tenants, tunnels, latest, infos: await agentInfos(api, tunnels) };
  }, [api]);
  const [editing, setEditing] = useState<{ id: string | null; draft: AgentInput } | null>(null);
  const [updating, setUpdating] = useState<Agent | null>(null);
  const [issued, setIssued] = useState<{ agent: Agent; issued: ContainerAuthClient } | null>(null);

  const d = data.data;
  const tenantName = (id: string) => d?.tenants.find((t) => t.id === id)?.name ?? '—';
  const versionOf = (a: Agent) =>
    d?.infos.get(tunnelOf(d.tunnels, a.clientId)?.clientId ?? a.clientId)?.version;
  const needle = q.trim().toLowerCase();
  const filtered = (d?.agents ?? [])
    .filter((a) => !tenant || a.tenantId === tenant)
    .filter((a) => {
      const t = tunnelOf(d?.tunnels, a.clientId);
      if (state === 'connected') return !!t?.isConnected;
      if (state === 'offline') return !t?.isConnected;
      if (state === 'outdated') return isOutdated(versionOf(a), d?.latest?.version);
      if (state === 'site') return a.remoteUpdatable === false;
      if (state === 'config') return a.configSource === 'current';
      if (state === 'secret') return (a.authMode ?? 'legacy') === 'legacy';
      if (state === 'auth') return a.authMode === 'both' || a.authMode === 'auth';
      return true;
    })
    .filter(
      (a) =>
        !needle ||
        [
          a.displayName,
          a.clientId,
          tenantName(a.tenantId),
          tunnelOf(d?.tunnels, a.clientId)?.machineName ?? '',
        ]
          .join(' ')
          .toLowerCase()
          .includes(needle)
    )
    .sort(
      (a, b) =>
        Number(!!tunnelOf(d?.tunnels, a.clientId)?.isConnected) -
          Number(!!tunnelOf(d?.tunnels, b.clientId)?.isConnected) ||
        a.displayName.localeCompare(b.displayName)
    );
  const tunnel = (a: Agent) => tunnelOf(d?.tunnels, a.clientId);
  const sort = useSort(
    filtered,
    {
      agent: { label: 'Agent', value: (a) => a.displayName },
      tenant: { label: 'Tenant', value: (a) => tenantName(a.tenantId) },
      tunnel: { label: 'Tunnel', value: (a) => (tunnel(a)?.isConnected ? 0 : 1) },
      machine: { label: 'Machine', value: (a) => tunnel(a)?.machineName },
      version: { label: 'Version', value: (a) => versionOf(a) ?? a.lastVersion },
      auth: { label: 'Sign-in', value: (a) => a.authMode ?? 'legacy' },
      routes: { label: 'Routes', value: (a) => tunnel(a)?.routeCount ?? 0, desc: true },
      channels: { label: 'Channels', value: (a) => tunnel(a)?.activeChannels ?? 0, desc: true },
    },
    useParam('sort', '')
  );
  const rows = sort.rows;

  const act = async (what: string, fn: () => Promise<unknown>) => {
    try {
      await fn();
      toast.success(what);
      await data.reload();
    } catch (e) {
      toast.error((e as Error).message);
    }
  };

  // An agent used to open in a drawer (`?agent=`); such links now go to its page.
  if (open) return <Navigate replace to={agPath(`/automation/agents/${encodeURIComponent(open)}`, target)} />;
  return (
    <Page
      title="Agents"
      description="Warehouse agents registered on the gateway: whether their tunnel is up, which version and machine they run, and their routes."
      actions={
        <>
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={data.loading}
            onClick={() => void data.reload()}
          >
            Refresh
          </Button>
          {canWrite && (
            <Button size="sm" onClick={() => setEditing({ id: null, draft: { ...EMPTY, tenantId: tenant } })}>
              New agent
            </Button>
          )}
        </>
      }
    >
      {toast.element}
      <div className="ag-toolbar">
        <Select
          aria-label="Tenant"
          value={tenant}
          onChange={setTenant}
          options={[
            { value: '', label: `All tenants (${d?.tenants.length ?? 0})` },
            ...(d?.tenants ?? []).map((t) => ({ value: t.id, label: t.name })),
          ]}
        />
        <Select aria-label="State" value={state} onChange={setState} options={STATES} />
        <TextField
          aria-label="Search"
          placeholder="Search name, id, machine"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <SortSelect sort={sort} />
      </div>
      <ErrorBox error={data.error} />
      <div className="nyk-card">
        <Loading loading={data.loading && !d} />
        {d?.latest && <p className="nyk-ui-hint">Latest agent version {d.latest.version}.</p>}
        {!!rows.length && (
          <TableScroller>
            <table className="nyk-ui-table nyk-cards" data-testid="ag-agents">
              <thead>
                <tr>
                  <SortTh sort={sort} by="agent" />
                  <SortTh sort={sort} by="tenant" />
                  <SortTh sort={sort} by="tunnel" />
                  <SortTh sort={sort} by="machine" />
                  <SortTh sort={sort} by="version" />
                  <th>Updates</th>
                  <SortTh sort={sort} by="auth" />
                  <SortTh sort={sort} by="routes" className="nyk-ui-num" />
                  <SortTh sort={sort} by="channels" className="nyk-ui-num" />
                </tr>
              </thead>
              {rows.map((a) => {
                const t = tunnelOf(d?.tunnels, a.clientId);
                return (
                  <tbody key={a.id} className="nyk-row-group" data-agent={a.clientId}>
                    <tr>
                      <td className="nyk-card-title">
                        <b>{a.displayName}</b>
                        <div className="nyk-ui-hint nyk-mono">{a.clientId}</div>
                      </td>
                      <td data-label="Tenant">{tenantName(a.tenantId)}</td>
                      <td data-label="Tunnel">
                        <ConnectionChip t={t} />
                      </td>
                      <td data-label="Machine">{t?.machineName ?? '—'}</td>
                      <td data-label="Version">
                        <VersionCell version={versionOf(a) ?? a.lastVersion} latest={d?.latest?.version} />
                      </td>
                      <td data-label="Updates">
                        <RemoteCell a={a} />
                      </td>
                      <td data-label="Sign-in">
                        <AuthChip a={a} />
                      </td>
                      <td className="nyk-ui-num" data-label="Routes">
                        <Link to={agPath(`/automation/routes?agent=${a.id}`, target)}>
                          {t?.routeCount ?? 0}
                        </Link>
                      </td>
                      <td className="nyk-ui-num" data-label="Channels">
                        {t?.activeChannels ?? 0}
                      </td>
                    </tr>
                    <ActionsRow colSpan={9}>
                      <Link
                        className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm"
                        to={agPath(`/automation/agents/${encodeURIComponent(a.id)}`, target)}
                      >
                        Details
                      </Link>
                      <Link
                        className="nyk-ui-btn nyk-ui-btn-ghost nyk-ui-btn-sm"
                        to={agPath(`/automation/logs?agent=${encodeURIComponent(a.clientId)}`, target)}
                      >
                        Logs
                      </Link>
                      {canWrite && (
                        <>
                          {t?.isConnected && (
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Restart the agent ${a.displayName}? Its tunnels drop until it is back.`}
                              onConfirm={() =>
                                void act(`Restart sent to ${a.displayName}`, () => api.restart(a.clientId))
                              }
                            >
                              Restart
                            </ConfirmButton>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => setUpdating(a)}>
                            Update
                          </Button>
                          {isContainer(a) && (a.authMode ?? 'legacy') === 'legacy' && (
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Create an AUTH client for the container ${a.displayName}? Its id and secret are shown once, for the container's environment; its shared secret keeps working.`}
                              onConfirm={() =>
                                void (async () => {
                                  try {
                                    setIssued({ agent: a, issued: await api.containerAuthClient(a.id) });
                                    await data.reload();
                                  } catch (e) {
                                    toast.error((e as Error).message);
                                  }
                                })()
                              }
                            >
                              AUTH client for container
                            </ConfirmButton>
                          )}
                          {canMoveToAuth(a, !!t?.isConnected) && (
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Move ${a.displayName} to AUTH? It gets its own AUTH client, sent over its connection; its shared secret keeps working.`}
                              onConfirm={() =>
                                void act(`${a.displayName} enrolled in AUTH`, () => api.moveToAuth(a.id))
                              }
                            >
                              Move to AUTH
                            </ConfirmButton>
                          )}
                          {a.authMode === 'both' && a.lastBearerAt && (
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Require AUTH for ${a.displayName}? Its shared secret is then refused (rollback: allow the secret again).`}
                              onConfirm={() =>
                                void act(`${a.displayName} requires AUTH`, () =>
                                  api.setAuthMode(a.id, 'auth')
                                )
                              }
                            >
                              Require AUTH
                            </ConfirmButton>
                          )}
                          {a.authMode === 'auth' && (
                            <ConfirmButton
                              size="sm"
                              variant="ghost"
                              message={`Allow ${a.displayName}'s shared secret again?`}
                              onConfirm={() =>
                                void act(`${a.displayName} may use its secret again`, () =>
                                  api.setAuthMode(a.id, 'both')
                                )
                              }
                            >
                              Allow secret
                            </ConfirmButton>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              setEditing({
                                id: a.id,
                                draft: {
                                  tenantId: a.tenantId,
                                  clientId: a.clientId,
                                  displayName: a.displayName,
                                  sharedSecret: '',
                                  allowedIpRanges: a.allowedIpRanges,
                                },
                              })
                            }
                          >
                            Edit
                          </Button>
                          <ConfirmButton
                            size="sm"
                            variant="ghost"
                            message={`Delete the agent ${a.displayName}? Only possible without routes.`}
                            onConfirm={() =>
                              void act(`Deleted ${a.displayName}`, () => api.deleteAgent(a.id))
                            }
                          >
                            Delete
                          </ConfirmButton>
                        </>
                      )}
                    </ActionsRow>
                  </tbody>
                );
              })}
            </table>
          </TableScroller>
        )}
        {d && !rows.length && <EmptyState title="No agents match." />}
      </div>
      {editing && d && (
        <AgentDialog
          api={api}
          id={editing.id}
          initial={editing.draft}
          tenants={d.tenants}
          onClose={() => setEditing(null)}
          onSaved={() => void data.reload()}
        />
      )}
      {updating && (
        <UpdateDialog
          api={api}
          agent={updating}
          latest={d?.latest?.version ?? null}
          connected={!!tunnelOf(d?.tunnels, updating.clientId)?.isConnected}
          onClose={() => setUpdating(null)}
          onDone={(m) => {
            toast.success(m);
            void data.reload();
          }}
        />
      )}
      {issued && <ContainerAuthDialog {...issued} onClose={() => setIssued(null)} />}
    </Page>
  );
}

/** The container's AUTH credentials, shown once: set them on the container and restart it. */
function ContainerAuthDialog({
  agent,
  issued,
  onClose,
}: {
  agent: Agent;
  issued: ContainerAuthClient;
  onClose: () => void;
}) {
  const lines = Object.entries(issued.environment)
    .map(([k, v]) => `${k}=${v}`)
    .join('\n');
  return (
    <Dialog
      open
      onClose={onClose}
      title={`AUTH client for ${agent.displayName}`}
      footer={<Button onClick={onClose}>Done</Button>}
    >
      <div className="ag-form">
        <p>
          Shown once. Add these to the container's environment and restart it: it then connects with an AUTH
          token. Its shared secret (<b>AGENT__TOKEN</b>) keeps working until you choose <b>Require AUTH</b>,
          so leave it in place.
        </p>
        <pre className="ag-secret" data-testid="ag-container-env">
          {lines}
        </pre>
        <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(lines)}>
          Copy
        </Button>
      </div>
    </Dialog>
  );
}

function AgentDialog({
  api,
  id,
  initial,
  tenants,
  onClose,
  onSaved,
}: {
  api: AutomationGwApi;
  id: string | null;
  initial: AgentInput;
  tenants: { id: string; name: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const save = async () => {
    if (!draft.tenantId) return setError('Pick a tenant.');
    if (draft.clientId.trim().length < 3 || draft.displayName.trim().length < 3)
      return setError('Agent id and name need at least 3 characters.');
    setBusy(true);
    setError(null);
    try {
      const body = { ...draft, clientId: draft.clientId.trim(), displayName: draft.displayName.trim() };
      const r = id ? await api.updateAgent(id, body) : await api.createAgent(body);
      onSaved();
      // a new agent, or a new secret: shown once, the agent needs it as AGENT__TOKEN
      if (!id || draft.sharedSecret) setSecret(r.sharedSecret ?? null);
      else onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={secret ? 'Agent saved' : id ? `Edit ${initial.displayName}` : 'New agent'}
      footer={
        secret ? (
          <Button onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button loading={busy} onClick={() => void save()}>
              Save
            </Button>
          </>
        )
      }
    >
      {secret ? (
        <div className="ag-form">
          <p>
            The agent connects with <b>AGENT__CLIENTID</b>={draft.clientId} and this secret as{' '}
            <b>AGENT__TOKEN</b>. Copy it now.
          </p>
          <div className="ag-secret" data-testid="ag-new-secret">
            {secret}
          </div>
          <Button size="sm" variant="secondary" onClick={() => void navigator.clipboard?.writeText(secret)}>
            Copy
          </Button>
        </div>
      ) : (
        <div className="ag-form">
          <ErrorBox error={error} />
          <Select
            label="Tenant"
            value={draft.tenantId}
            onChange={(v) => setDraft({ ...draft, tenantId: v })}
            options={[
              { value: '', label: 'Pick a tenant' },
              ...tenants.map((t) => ({ value: t.id, label: t.name })),
            ]}
          />
          <TextField
            label="Agent id"
            hint="AGENT__CLIENTID on the agent."
            value={draft.clientId}
            onChange={(e) => setDraft({ ...draft, clientId: e.target.value })}
          />
          <TextField
            label="Name"
            value={draft.displayName}
            onChange={(e) => setDraft({ ...draft, displayName: e.target.value })}
          />
          <TextField
            label={id ? 'New shared secret' : 'Shared secret'}
            hint={
              id
                ? 'Empty keeps the current one. A new one disconnects the agent until it uses it.'
                : 'Empty = generated.'
            }
            type="password"
            autoComplete="new-password"
            value={draft.sharedSecret ?? ''}
            onChange={(e) => setDraft({ ...draft, sharedSecret: e.target.value })}
          />
          <ListField
            label="Allowed IP ranges"
            hint="Comma-separated CIDRs the agent may connect from; empty = anywhere."
            value={draft.allowedIpRanges}
            onChange={(v) => setDraft({ ...draft, allowedIpRanges: v })}
          />
        </div>
      )}
    </Dialog>
  );
}

function UpdateDialog({
  api,
  agent,
  latest,
  connected,
  onClose,
  onDone,
}: {
  api: AutomationGwApi;
  agent: Agent;
  latest: string | null;
  connected: boolean;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const [version, setVersion] = useState('');
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const send = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.triggerUpdate(agent.clientId, {
        targetVersion: version.trim() || undefined,
        force: force || undefined,
      });
      onDone(r.message ?? `Update to ${r.targetVersion ?? 'latest'}: ${r.status}`);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      open
      onClose={onClose}
      title={`Update ${agent.displayName}`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={busy} onClick={() => void send()}>
            Send update
          </Button>
        </>
      }
    >
      <div className="ag-form">
        <ErrorBox error={error} />
        {!connected && (
          <p className="nyk-ui-hint">
            The agent is offline: the update waits (10 min) for a standby agent to poll, or for the agent to
            reconnect.
          </p>
        )}
        <TextField
          label="Version"
          hint={`Empty = latest${latest ? ` (${latest})` : ''}. An older version installs only with Force.`}
          value={version}
          onChange={(e) => setVersion(e.target.value)}
        />
        <Switch checked={force} onChange={setForce} label="Force (also the same or an older version)" />
      </div>
    </Dialog>
  );
}

/** One agent's page (`/automation/agents/:id`): loads the agent and its tunnel, then shows `AgentView`. */
export function AgentPage() {
  const { id = '' } = useParams();
  const target = useAutomationTarget();
  const api = useMemo(() => automationGw(target), [target]);
  const canWrite = useAutomationWrite();
  const data = useAsync(async () => {
    const [agents, tunnels] = await Promise.all([api.agents(), api.tunnels()]);
    return { agent: agents.find((a) => a.id === id) ?? null, tunnels };
  }, [api, id]);
  const listPath = agPath('/automation/agents', target);
  if (data.data?.agent)
    return (
      <AgentView
        key={data.data.agent.id}
        api={api}
        agent={data.data.agent}
        tunnel={tunnelOf(data.data.tunnels, data.data.agent.clientId)}
        canWrite={canWrite}
        listPath={listPath}
      />
    );
  return (
    <Page
      title={
        <>
          <Link to={listPath} className="nyk-back">
            Agents
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          {id}
        </>
      }
    >
      <ErrorBox error={data.error} />
      <Loading loading={data.loading} />
      {data.data && <EmptyState card title="No agent with this id in this environment." />}
    </Page>
  );
}

/** One agent: sign-in and update state, tunnel, system info (disks, network), connection history; diagnostics, new secret. */
function AgentView({
  api,
  agent,
  tunnel,
  canWrite,
  listPath,
}: {
  api: AutomationGwApi;
  agent: Agent;
  tunnel: TunnelStatus | undefined;
  canWrite: boolean;
  listPath: string;
}) {
  const info = useAsync(
    () => (tunnel?.isConnected ? api.info(agent.clientId) : Promise.resolve(null)),
    [api, agent.clientId, tunnel?.isConnected]
  );
  const history = useAsync(() => api.history(agent.clientId, 30), [api, agent.clientId]);
  const [secret, setSecret] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const i = info.data;
  const run = async (fn: () => Promise<void>) => {
    setError(null);
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    }
  };
  return (
    <Page
      title={
        <>
          <Link to={listPath} className="nyk-back">
            Agents
          </Link>
          <span className="nyk-crumb-sep"> / </span>
          {agent.displayName}
        </>
      }
      description={<span className="nyk-mono">{agent.clientId}</span>}
      actions={
        <>
          {tunnel?.isConnected && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() =>
                void run(async () => {
                  const r = await api.diagnostics(agent.clientId);
                  setNote(r.message);
                  setTimeout(() => void info.reload(), 4000);
                })
              }
            >
              Run diagnostics
            </Button>
          )}
          {canWrite && (
            <ConfirmButton
              size="sm"
              variant="danger"
              message={`Issue a new shared secret for ${agent.displayName}? The old one stops working at once${agent.authMode === 'auth' ? '' : ' and the agent stays offline until it is configured with the new one on site'}.`}
              onConfirm={() =>
                void run(async () => setSecret((await api.rotateSecret(agent.id)).sharedSecret ?? ''))
              }
            >
              New secret
            </ConfirmButton>
          )}
        </>
      }
    >
      <ErrorBox error={error || info.error} />
      {note && <p className="nyk-ui-hint">{note}</p>}
      {secret !== null && (
        <div className="ag-section">
          <h3>New shared secret</h3>
          <p className="nyk-ui-hint">
            Shown once. The agent needs it as AGENT__TOKEN (or Token in appsettings.json).
          </p>
          <div className="ag-secret" data-testid="ag-secret">
            {secret || '—'}
          </div>
        </div>
      )}
      <div className="ag-section">
        <h3>Sign-in and updates</h3>
        <table className="nyk-ui-table nyk-kv" data-testid="ag-auth">
          <tbody>
            <tr>
              <th>Sign-in</th>
              <td>
                <AuthChip a={agent} />
              </td>
            </tr>
            {agent.authClientId && (
              <tr>
                <th>AUTH client</th>
                <td>
                  <span className="nyk-mono">{agent.authClientId}</span>
                  {canWrite && (
                    <ConfirmButton
                      size="sm"
                      variant="ghost"
                      message={`Forget ${agent.displayName}'s AUTH client? It goes back to its shared secret at the next connect (the AUTH client itself stays; delete it under Identity if it is no longer needed).`}
                      onConfirm={() =>
                        void run(async () => {
                          await api.removeAuthClient(agent.id);
                          setNote(
                            'AUTH client removed: the agent uses its shared secret from its next connect.'
                          );
                        })
                      }
                    >
                      Remove
                    </ConfirmButton>
                  )}
                </td>
              </tr>
            )}
            <tr>
              <th>Last AUTH token</th>
              <td>{agent.lastBearerAt ? formatDateTime(agent.lastBearerAt) : '—'}</td>
            </tr>
            <tr>
              <th>Updates</th>
              <td>
                <RemoteCell a={agent} />
              </td>
            </tr>
            <tr>
              <th>Config from</th>
              <td>{agent.configSource ?? 'not reported (older agent)'}</td>
            </tr>
            <tr>
              <th>Last seen</th>
              <td>
                {agent.lastSeenAt ? formatDateTime(agent.lastSeenAt) : '—'}
                {agent.lastVersion && <span className="nyk-ui-hint"> · {agent.lastVersion}</span>}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div className="ag-section">
        <h3>Tunnel</h3>
        <table className="nyk-ui-table nyk-kv">
          <tbody>
            <tr>
              <th>Agent id</th>
              <td className="nyk-mono">{agent.clientId}</td>
            </tr>
            <tr>
              <th>State</th>
              <td>
                <ConnectionChip t={tunnel} />
              </td>
            </tr>
            <tr>
              <th>Allowed IPs</th>
              <td>{agent.allowedIpRanges.join(', ') || 'anywhere'}</td>
            </tr>
            {tunnel?.isConnected && (
              <>
                <tr>
                  <th>Control traffic</th>
                  <td>
                    {formatBytes(tunnel.controlBytesSent)} sent · {formatBytes(tunnel.controlBytesReceived)}{' '}
                    received
                  </td>
                </tr>
                <tr>
                  <th>File transfers</th>
                  <td>
                    {formatBytes(tunnel.fileTransferBytesSent)} sent ·{' '}
                    {formatBytes(tunnel.fileTransferBytesReceived)} received
                  </td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
      {info.loading && !i && <Loading loading />}
      {i && (
        <>
          <div className="ag-section">
            <h3>System</h3>
            <table className="nyk-ui-table nyk-kv" data-testid="ag-info">
              <tbody>
                <tr>
                  <th>Version</th>
                  <td>{i.version}</td>
                </tr>
                <tr>
                  <th>Machine</th>
                  <td>{i.machineName}</td>
                </tr>
                <tr>
                  <th>OS</th>
                  <td>
                    {i.osDescription} ({i.osArchitecture}) · .NET {i.dotNetVersion}
                  </td>
                </tr>
                <tr>
                  <th>CPU / memory</th>
                  <td>
                    {i.processorCount} cores · {formatBytes(i.availableMemoryMB * 1048576)} free of{' '}
                    {formatBytes(i.totalMemoryMB * 1048576)} · agent uses{' '}
                    {formatBytes(i.workingSetMB * 1048576)}
                  </td>
                </tr>
                <tr>
                  <th>Started</th>
                  <td>
                    {formatDateTime(i.startTime)} ({formatRelative(i.startTime)})
                  </td>
                </tr>
                <tr>
                  <th>Reported</th>
                  <td>{formatRelative(i.receivedAt)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {!!i.diskInfo?.length && (
            <div className="ag-section">
              <h3>Disks</h3>
              <TableScroller>
                <table className="nyk-ui-table">
                  <thead>
                    <tr>
                      <th>Disk</th>
                      <th className="nyk-ui-num">Free</th>
                      <th className="nyk-ui-num">Size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {i.diskInfo.map((k) => (
                      <tr key={k.name}>
                        <td>
                          {k.name} {k.label && <span className="nyk-ui-hint">{k.label}</span>}
                        </td>
                        <td
                          className={`nyk-ui-num${k.availableFreeMB / Math.max(1, k.totalSizeMB) < 0.1 ? ' nyk-text-red' : ''}`}
                        >
                          {formatBytes(k.availableFreeMB * 1048576)}
                        </td>
                        <td className="nyk-ui-num">{formatBytes(k.totalSizeMB * 1048576)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            </div>
          )}
          {!!i.networkInterfaces?.length && (
            <div className="ag-section">
              <h3>Network</h3>
              <TableScroller>
                <table className="nyk-ui-table">
                  <thead>
                    <tr>
                      <th>Interface</th>
                      <th>Addresses</th>
                    </tr>
                  </thead>
                  <tbody>
                    {i.networkInterfaces.map((n) => (
                      <tr key={n.name}>
                        <td>
                          {n.name}
                          <div className="nyk-ui-hint">{n.description}</div>
                        </td>
                        <td>{[...n.iPv4Addresses, ...n.iPv6Addresses].join(', ') || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroller>
            </div>
          )}
        </>
      )}
      <div className="ag-section">
        <h3>Connections, last 30 days</h3>
        <ErrorBox error={history.error} />
        {history.data && !history.data.length && <p className="nyk-ui-hint">No connections.</p>}
        {!!history.data?.length && (
          <TableScroller>
            <table className="nyk-ui-table" data-testid="ag-history">
              <thead>
                <tr>
                  <th>Connected</th>
                  <th>For</th>
                  <th>Machine</th>
                  <th>Version</th>
                  <th>Ended</th>
                </tr>
              </thead>
              <tbody>
                {history.data.map((h) => (
                  <tr key={h.id}>
                    <td>{formatDateTime(h.connectedAt)}</td>
                    <td>
                      {formatDuration(
                        (h.disconnectedAt ? new Date(h.disconnectedAt).getTime() : Date.now()) -
                          new Date(h.connectedAt).getTime()
                      )}
                    </td>
                    <td>{h.machineName}</td>
                    <td>{h.agentVersion ?? '—'}</td>
                    <td>{h.disconnectedAt ? (h.disconnectReason ?? 'disconnected') : 'still connected'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableScroller>
        )}
      </div>
    </Page>
  );
}
